import React, { useContext, useState, FC, useEffect } from 'react';
import { Translation, Key } from '../Model/TranslationOutput';
import TranslationService from '../services/TranslationService';
import { UseConfigContext } from "./ConfigContext";
import { UseToastContext, toastType } from './ToastContext';
import { CLPrestKey, CodeListPrest, SalarySimFormCodeList, CodeListKey, CodeListKeyLong, FieldConstraintType } from '../Model/CodeListOutput';
import { UseTranslationContext } from './TranslationContext';
import { CodeListPrestService, CodeListFormService, JCService, PrefixService, FieldConstraintsService } from '../services/CodeListService';

interface ICodeListContext {
    codeListPrestaPA5: CodeListPrest,
    updateCLPrestaPA5: (params: CodeListPrest) => void,
    getCLPrestaKey: (keyId: string) => string,

    codeListForm: SalarySimFormCodeList,
    updateCodeListForm: (params: SalarySimFormCodeList) => void,
    getCodeListKey: (keyId: string, keyValNum : number, lang?: string) => string,
    getJointComiteeKey: (keyValNum: number, shortKey?: boolean, lang?: string) => string,

    codeListJointCommittee: Array<CodeListKeyLong>,
    codeListNSSOPrefix: Array<CodeListKey>,
    refreshCodeListNSSOPrefix: (currentJcNumber: number, currentContractType: number) => void,

    currentSelectedContract: number,
    setCurrentSelectedContract: (newContract: number) => void,

    prestaUpdateInProgress: boolean,
    setPrestaUpdateInProgress: (inProgress: boolean) => void,
    isLoaded: boolean,
    forceJointComiteeUpdate: () => void,

    // specfic field restrictions on contract and onss
    restrictedFieldEndOfYearBonus: Array<string>,
    restrictedFieldGrossSubmitted: Array<string>,
}

const defaultCodeListContext = {
    codeListPrestaPA5: new CodeListPrest(),
    updateCLPrestaPA5: (params: CodeListPrest) => { return; },
    getCLPrestaKey: () => { return ''; },

    codeListForm: new SalarySimFormCodeList(),
    updateCodeListForm: (params: SalarySimFormCodeList) => { return; },
    getCodeListKey: (keyId: string, keyValNum : number, lang?: string) => { return ''; },
    getJointComiteeKey: (keyValNum: number, shortKey?: boolean, lang?: string) => { return ''; },

    codeListJointCommittee: new Array<CodeListKeyLong>(),
    codeListNSSOPrefix: new Array<CodeListKey>(),
    refreshCodeListNSSOPrefix: (currentJcNumber: number, currentContractType: number) => { return; },

    currentSelectedContract: 0,
    setCurrentSelectedContract: (newContract: number) => { return; },

    prestaUpdateInProgress: false,
    setPrestaUpdateInProgress: (inProgress: boolean) => {return;},
    isLoaded: false,
    forceJointComiteeUpdate: () => {},

    // specfic field restrictions on contract and onss
    restrictedFieldEndOfYearBonus: new Array<string>(),
    restrictedFieldGrossSubmitted: new Array<string>(),
}

interface ITranslationProvider {
    children?: React.ReactNode,
}

const CodeListContext = React.createContext<ICodeListContext>(defaultCodeListContext);
export const UseCodeListContext = () => useContext(CodeListContext);

export const CodeListContextProvider: FC<ITranslationProvider> = ({ children }) => {

    // init and useState
    const { getServiceUrl, isConfigLoaded } = UseConfigContext();
    const { currentLanguage, GetErrorMessage } = UseTranslationContext();
    const { popToast } = UseToastContext();

    const [codeListForm, setCodeListForm] = useState(defaultCodeListContext.codeListForm);
    const [codeListPrestaPA5, setCodeListPrestaPA5] = useState(defaultCodeListContext.codeListPrestaPA5);
    const [codeListJointCommittee, setCodeListJointCommittee] = useState(defaultCodeListContext.codeListJointCommittee);
    const [codeListNSSOPrefix, setCodeListNSSOPrefix] = useState(defaultCodeListContext.codeListNSSOPrefix);

    const [prestaUpdateInProgress, setPrestaUpdateInProgress] = useState(defaultCodeListContext.prestaUpdateInProgress);

    const [isLoaded, setIsLoaded] = useState(defaultCodeListContext.isLoaded);

    const [currentSelectedContract, setCurrentSelectedContract] = useState(defaultCodeListContext.currentSelectedContract);

    // specfic field restrictions on contract and onss
    const [restrictedFieldEndOfYearBonus, setRestrictedFieldEndOfYearBonus] = useState(defaultCodeListContext.restrictedFieldEndOfYearBonus);
    const [restrictedFieldGrossSubmitted, setRestrictedFieldGrossSubmitted] = useState(defaultCodeListContext.restrictedFieldGrossSubmitted);
    

    // code lists functions
    const updateCodeListForm = (params: SalarySimFormCodeList) => {
        setCodeListForm(params);
    }

    const getCodeListKey = (keyId: string, keyValNum : number, lang?: string) => {

        var codeListKey = "";
        if (lang == undefined) lang = currentLanguage

        switch (keyId) {
            case 'ETAT_CIVIL_IBRUNET':
                codeListKey = getFormCodeListKey(codeListForm.clMaritalStatus, keyValNum, lang);
                break;
            case 'IBRUNET_UNITE_LOCALE':
                codeListKey = getFormCodeListKey(codeListForm.clLocalUnit, keyValNum, lang);
                break;
            case 'CONJOINT_IBRUNET':
                codeListKey = getFormCodeListKey(codeListForm.clPartner, keyValNum, lang);
            break;
            
            case 'TYPE_CONTRAT_IBRUNET':
                codeListKey = getFormCodeListKey(codeListForm.clContr, keyValNum, lang);
            break;
            
            case 'TYPE_CONTRAT_IBRUNET_PLUS':
                codeListKey = getFormCodeListKey(codeListForm.clContrPlus, keyValNum, lang);
            break;
            
            case 'IBRUNET_CD_ENGAGEMENT':
                codeListKey = getFormCodeListKey(codeListForm.clCdHire, keyValNum, lang);
            break;
            
            case 'TEMPS_PLEIN_PARTIEL_IBRUNET':
                codeListKey = getFormCodeListKey(codeListForm.clFullTime, keyValNum, lang);
            break;
            
            case 'SOUMIS_PRECOMPTE_IBRUNET_PLUS':
                codeListKey = getFormCodeListKey(codeListForm.clDeduction, keyValNum, lang);
            break;
            
            case 'SOUMIS_ONSS_IBRUNET_PLUS':
                codeListKey = getFormCodeListKey(codeListForm.clONSSSubmit, keyValNum, lang);
            break;
            
            case 'EFFECTIF_EMPL_IBRUNET_PLUS':
                codeListKey = getFormCodeListKey(codeListForm.clEmplWorkforce, keyValNum, lang);
            break;
        }

        return (codeListKey != "")?codeListKey:keyId;
    }

    const getFormCodeListKey = (keyList : Array<CodeListKey>, keyValNum: number, lang?: string) => {
        
        if (lang == undefined) lang = currentLanguage;
        var codeListKey = "";
        if (keyList != null) {
            var foundKey = keyList.find(myKey => myKey.keyValNum == keyValNum);
            if (foundKey != null) {
                if (lang == "fr") {
                    return foundKey.keyFr;
                }
                else if (lang == "nl") {
                    return foundKey.keyNl;
                }
                else {
                    return foundKey.keyX;
                }
            }
            else {
                return codeListKey;
            }
        }

        return codeListKey;
    };

    const getJointComiteeKey = (keyValNum: number, shortKey?: boolean, lang?: string) => {
        
        if (lang == undefined) lang = currentLanguage;
        if( shortKey == undefined ) shortKey = true;

        var codeListKey = "";
        if (codeListJointCommittee != null) {
            var foundKey = codeListJointCommittee.find(myKey => myKey.keyValNum == keyValNum);
            if (foundKey != null) {
                if (lang == "fr") {
                    return shortKey?foundKey.keyFr:foundKey.keyFrLong;
                }
                else if (lang == "nl") {
                    return shortKey?foundKey.keyNl:foundKey.keyNlLong;
                }
                else {
                    return shortKey?foundKey.keyX:foundKey.keyXLong;
                }
            }
            else {
                return codeListKey;
            }
        }

        return codeListKey;
    };


    const updateCLPrestaPA5 = (params: CodeListPrest) => {
        setCodeListPrestaPA5(params);
    };

    const getCLPrestaKey = (keyId: string) => {

        if (codeListPrestaPA5.keys != null) {
            var foundKey = codeListPrestaPA5.keys.find(myKey => myKey.cd_prest == keyId);
            if (foundKey != null) {
                if (currentLanguage == "fr") {
                    return foundKey.lb_prest_f;
                }
                else if (currentLanguage == "nl") {
                    return foundKey.lb_prest_n;
                }
                else {
                    return foundKey.lb_prest_x;
                }
            }
            else {
                return keyId;
            }
        }

        return keyId;
    }

    // MAIN HOOK AFTER CONFIG LOADED
    useEffect(() => {
        if (isConfigLoaded == false)
            return;

        // MAIN CODE LISTS
        var salarysimFormCodeLists = CodeListFormService(getServiceUrl());

        salarysimFormCodeLists.then(
            Result => {
                if (typeof (Result) == 'string') {
                    popToast(toastType.Error, GetErrorMessage(currentLanguage));
                }
                else {
                    updateCodeListForm(Result as SalarySimFormCodeList);
                    setIsLoaded(true);
                }

                return Result;
            })
            .catch(e => {
                popToast(toastType.Error, GetErrorMessage(currentLanguage));
                return ("error " + e);
            });


        // PREST CODE LIST FOR RESULT OBJECT
        var salarySimPrestaCodeListData = CodeListPrestService(getServiceUrl());

        salarySimPrestaCodeListData.then(
            Result => {
                if (typeof (Result) == 'string') {
                    popToast(toastType.Error, GetErrorMessage(currentLanguage));
                }
                else {
                    updateCLPrestaPA5(Result as CodeListPrest);
                    setIsLoaded(true);
                }

                return Result;
            })
            .catch(e => {
                popToast(toastType.Error, GetErrorMessage(currentLanguage));
                return ("error " + e);
            });

        // Selecting default contract
        setCurrentSelectedContract(1);



        // Field constraints

        var constraints = FieldConstraintsService(getServiceUrl());

        constraints.then(
            Result => {
                if (typeof (Result) == 'string') {
                    popToast(toastType.Error, GetErrorMessage(currentLanguage));
                }
                else {
                    setRestrictedFieldEndOfYearBonus(Result.filter(x => x.constraintType === FieldConstraintType.EndOfYearBonus).map(x => x.constraintValue));
                    setRestrictedFieldGrossSubmitted(Result.filter(x => x.constraintType === FieldConstraintType.GrossSubmittedAmount).map(x => x.constraintValue));
                }

                return Result;
            })
            .catch(e => {
                popToast(toastType.Error, GetErrorMessage(currentLanguage));
                return ("error " + e);
            });
        
    }, [isConfigLoaded]);

    // default contracts from the API
    useEffect(() => {
        if (isConfigLoaded == false)
            return;

        if (codeListForm != undefined && codeListForm.clContrPlus != undefined && codeListForm.clContrPlus.length > 0) {
            let contractType = codeListForm.clContrPlus[0].keyValNum;
            setCurrentSelectedContract(contractType);
        }
    }, [codeListForm]);

    // CONTRACT
    // Current selected contract trigger
    useEffect(() => {
        if (isConfigLoaded == false)
            return;

        JCService(getServiceUrl(), currentSelectedContract)
        .then(output => {
          if (typeof(output) == "string") throw output;
          else {
            setCodeListJointCommittee(output);
          }
        })
    }, [currentSelectedContract])

    // JOINT COMITEE - UPDATE JOINT COMITEES
    // forcing a contract update ( used when importing data with the same id than the one currently selected )
    const forceJointComiteeUpdate = () => {
        if (isConfigLoaded == false)
            return;
            JCService(getServiceUrl(), currentSelectedContract)
        .then(output => {
          if (typeof(output) == "string") throw output;
          else {
            setCodeListJointCommittee(output);
          }
        })
    }

    // JOINT COMITEE TRIGGER - UPDATING NSSO PREFIX
    // current joint committee trigger
    useEffect(() => {
        if (isConfigLoaded == false || prestaUpdateInProgress == true)
            return;

        if (codeListJointCommittee != undefined && codeListJointCommittee.length > 0) {
            let contractType = currentSelectedContract;
            let jc = codeListJointCommittee[0].keyValNum;
            refreshCodeListNSSOPrefix(jc, contractType);
        }
    }, [codeListJointCommittee]);



    // NSSO PREFIX - UPDATE
    // update NSSO prefix function
    const refreshCodeListNSSOPrefix = (currentJcNumber: number, currentContractType: number) => {
        PrefixService(getServiceUrl(), currentJcNumber.toString(), currentContractType)
        .then(output => {
          if (typeof(output) == "string") throw output;
          else {
            setCodeListNSSOPrefix(output);
          }
        })
    }
    
    // OUTPUT
    return (
        <CodeListContext.Provider value={{
            codeListPrestaPA5,
            updateCLPrestaPA5,
            getCLPrestaKey,

            codeListForm,
            updateCodeListForm,
            getCodeListKey,
            getJointComiteeKey,

            codeListJointCommittee,
            codeListNSSOPrefix,
            refreshCodeListNSSOPrefix,
            currentSelectedContract,
            setCurrentSelectedContract,
            prestaUpdateInProgress,
            setPrestaUpdateInProgress,
            isLoaded,
            forceJointComiteeUpdate,
            restrictedFieldEndOfYearBonus,
            restrictedFieldGrossSubmitted
        }}>
            {children}
        </CodeListContext.Provider>
    )
}